<script lang="ts">
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { UrlUtil } from '$lib/utils/url.util';
	import { watch } from 'runed';
	import IconChevronLeft from '~icons/mdi/chevron-left';
	import IconChevronRight from '~icons/mdi/chevron-right';

	let {
		images,
		activeImage = $bindable()
	}: {
		images: CoverImage[];
		activeImage?: CoverImage;
	} = $props();

	let currentIndex = $state<number>(0);

	watch(
		() => images,
		() => {
			currentIndex = 0;
		}
	);
	watch(
		() => currentIndex,
		() => {
			if (currentIndex >= images.length) {
				currentIndex = 0;
			}
			activeImage = images[currentIndex];
		}
	);

	function shouldLoad(i: number): boolean {
		if (i === currentIndex) return true;
		const nextIndex = (currentIndex + 1) % images.length;
		const prevIndex = (currentIndex - 1 + images.length) % images.length;
		return i === nextIndex || i === prevIndex;
	}

	function previousImage(): void {
		if (!images.length) return;
		currentIndex = (currentIndex - 1 + images.length) % images.length;
	}

	function nextImage(): void {
		if (!images.length) return;
		currentIndex = (currentIndex + 1) % images.length;
	}

	function isCurrentlyShown(i: number): boolean {
		return i === currentIndex;
	}
</script>

<div
	class="group flex h-full w-full flex-col gap-3 select-none"
	role="region"
	aria-roledescription="carousel"
	aria-label="Image Carousel"
>
	<div class="relative min-h-0 w-full flex-1">
		<div id="carousel-items" class="absolute inset-0 flex items-center justify-center">
			{#each images as img, i (img.filename)}
				<div
					class={{
						'absolute inset-0 flex items-center justify-center transition-opacity duration-250': true,
						'z-10 opacity-100': isCurrentlyShown(i),
						'pointer-events-none z-0 opacity-0': !isCurrentlyShown(i)
					}}
					role="group"
					aria-roledescription="slide"
					aria-label="{i + 1} of {images.length}"
					aria-hidden={!isCurrentlyShown(i)}
				>
					<img
						class="h-auto max-h-full w-auto max-w-full rounded-box shadow-md"
						src={i === 0 || shouldLoad(i) ? UrlUtil.getFullImageUrl(img) : undefined}
						data-src={UrlUtil.getFullImageUrl(img)}
						alt={img.altText}
						width={img.width}
						height={img.height}
						fetchpriority={i === 0 ? 'high' : 'auto'}
						loading={i === 0 ? 'eager' : 'lazy'}
						draggable="false"
					/>

					<noscript>
						<img src={UrlUtil.getFullImageUrl(img)} alt={img.altText} />
					</noscript>
				</div>
			{/each}
		</div>

		{#if images.length > 1}
			{@render PreviousBtn()}
			{@render NextButton()}
		{/if}
	</div>

	{#if images.length > 1}
		{@render DotsIndicator()}
	{/if}
</div>

{#snippet PreviousBtn()}
	<button
		class="group btn absolute top-1/2 left-3 z-20 btn-circle size-9 -translate-y-1/2 bg-black/45 btn-ghost text-white/90 transition-[background-color,transform,opacity] duration-150 ease-out hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-95 active:bg-black/80 sm:left-5 lg:opacity-0 lg:group-hover:opacity-100"
		aria-label="Previous slide"
		aria-controls="carousel-items"
		onclick={previousImage}
	>
		<IconChevronLeft class="text-xl" />
	</button>
{/snippet}

{#snippet NextButton()}
	<button
		class="group btn absolute top-1/2 right-3 z-20 btn-circle size-9 -translate-y-1/2 bg-black/45 btn-ghost text-white/90 transition-[background-color,transform,opacity] duration-150 ease-out hover:bg-black/65 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 active:scale-95 active:bg-black/80 sm:right-5 lg:opacity-0 lg:group-hover:opacity-100"
		aria-label="Next slide"
		aria-controls="carousel-items"
		onclick={nextImage}
	>
		<IconChevronRight class="text-xl" />
	</button>
{/snippet}

{#snippet DotsIndicator()}
	<div
		class="flex w-full flex-wrap items-center justify-center gap-2 px-4 transition-opacity duration-300 group-hover:opacity-100 lg:opacity-0"
	>
		{#each images as img, i (img.filename)}
			<button
				class={{
					'h-[2vh] w-[2vh] rounded-full transition-all duration-300 lg:h-[1.5vh] lg:w-[1.5vh]': true,
					'w-[3vh] bg-white lg:w-[2.5vh]': isCurrentlyShown(i),
					'bg-white/50 hover:bg-white/80': !isCurrentlyShown(i)
				}}
				aria-label="Go to slide {i + 1}"
				aria-controls="carousel-items"
				aria-current={isCurrentlyShown(i) ? 'true' : 'false'}
				onclick={() => (currentIndex = i)}
			></button>
		{/each}
	</div>
{/snippet}
